import React, { useState } from 'react';
import { Table, Tag, Button, Space, Tooltip, App } from 'antd';
import { useMemo } from 'react';
import {
  DownloadOutlined,
  DeleteOutlined,
  SaveOutlined,
  ClockCircleOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  FileOutlined,
  CloudOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useFileList, useFileOperations } from '../../hooks/useFileList';
import type { FileListFilters } from '../../hooks/useFileList';
import type { ColumnsType } from 'antd/es/table';
import { withDevModalFilePath } from '../common';
import { trpc } from '../../lib/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { usePermission } from '../../hooks/usePermission';

interface FileInfo {
  id: string;
  name: string;
  originalName?: string | null;
  size: number;
  fileType: 'photo' | 'video' | 'document' | 'other';
  storageClass: string | null;
  uploadedAt: string | Date;
  uploadedBy: string | null;
  lastAccessedAt: string | Date | null;
  accessCount: number;
  archivedAt: string | Date | null;
}

const { Modal } = withDevModalFilePath('client/src/components/storage/FileListTable.tsx');

/**
 * ファイルアイコンの取得
 */
function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'photo':
      return <FileImageOutlined style={{ color: '#1890ff', fontSize: 18 }} />;
    case 'video':
      return <VideoCameraOutlined style={{ color: '#722ed1', fontSize: 18 }} />;
    case 'document':
      return <FileTextOutlined style={{ color: '#fa8c16', fontSize: 18 }} />;
    default:
      return <FileOutlined style={{ color: '#999', fontSize: 18 }} />;
  }
}

/**
 * ファイルサイズのフォーマット
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
  return `${(bytes / (1024 ** 3)).toFixed(2)} GB`;
}

/**
 * 日付のフォーマット
 */
function formatDate(date: string | Date | null): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * 経過日数の計算
 */
function getDaysAgo(date: string | Date | null): number {
  if (!date) return -1;
  return Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
}

interface FileListTableProps {
  filters: FileListFilters;
}

/**
 * ファイル一覧テーブル
 */
export function FileListTable({ filters }: FileListTableProps) {
  const { message } = App.useApp();
  const trpcUtils = trpc.useUtils();
  const { data, isLoading } = useFileList(filters);
  const fileRows = useMemo<FileInfo[]>(
    () => (Array.isArray(data?.files) ? (data.files as unknown as FileInfo[]) : []),
    [data]
  );
  const { archiveFile, bulkDelete, recordAccess } = useFileOperations();
  const { isAdmin } = usePermission();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const handleDownload = async (file: FileInfo) => {
    if (!isAdmin) {
      message.error('この操作は管理者のみ可能です');
      return;
    }
    try {
      setDownloadingFileId(file.id);
      const { url } = await trpcUtils.storage.getDownloadUrl.fetch({
        fileId: file.id,
        action: 'download',
      });
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName ?? file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      recordAccess.mutate({
        fileId: file.id,
        accessType: 'download',
      });
      message.success(`${file.name} のダウンロードを開始しました`);
    } catch (error) {
      message.error(`ダウンロードに失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDelete = (file: FileInfo) => {
    if (!isAdmin) {
      message.error('この操作は管理者のみ可能です');
      return;
    }
    Modal.confirm({
      title: 'ファイルを削除しますか？',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <div>{file.name}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
            サイズ: {formatFileSize(file.size)}
          </div>
        </div>
      ),
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        bulkDelete.mutate({
          fileIds: [file.id],
          confirmDeletion: true,
        });
      },
    });
  };

  const handleArchive = (file: FileInfo) => {
    Modal.confirm({
      title: 'ファイルをアーカイブしますか？',
      icon: <SaveOutlined />,
      content: (
        <div>
          <div>{file.name}</div>
          <div style={{ marginTop: 12, fontSize: 13 }}>
            アーカイブ後も即座にアクセス可能です（Glacier IR）
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#52c41a' }}>
            💰 コスト84%削減
          </div>
        </div>
      ),
      okText: 'アーカイブ',
      cancelText: 'キャンセル',
      onOk: () => {
        archiveFile.mutate({
          fileId: file.id,
          targetStorageClass: 'GLACIER_IR',
          immediate: false,
        });
      },
    });
  };

  const handleBulkDelete = () => {
    if (!isAdmin) {
      message.error('この操作は管理者のみ可能です');
      return;
    }
    Modal.confirm({
      title: `${selectedFiles.length}件のファイルを削除しますか？`,
      icon: <ExclamationCircleOutlined />,
      content: '削除したファイルは復元できません。',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        bulkDelete.mutate({
          fileIds: selectedFiles,
          confirmDeletion: true,
        }, {
          onSuccess: () => {
            setSelectedFiles([]);
          },
        });
      },
    });
  };

  const columns: ColumnsType<FileInfo> = [
    {
      title: 'ファイル名',
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      render: (name: string, record: FileInfo) => (
        <Space>
          {getFileIcon(record.fileType)}
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: 'サイズ',
      dataIndex: 'size',
      key: 'size',
      width: '10%',
      render: (size: number) => formatFileSize(size),
      sorter: true,
    },
    {
      title: 'ストレージ',
      dataIndex: 'storageClass',
      key: 'storageClass',
      width: '15%',
      render: (storageClass: string | null) => {
        if (!storageClass || storageClass === 'STANDARD') {
          return (
            <Tag color="blue" icon={<CloudOutlined />}>
              アクティブ
            </Tag>
          );
        } else if (storageClass === 'GLACIER_IR') {
          return (
            <Tooltip title="コスト84%削減中・即時アクセス可能">
              <Tag color="green" icon={<SaveOutlined />}>
                アーカイブ済み
              </Tag>
            </Tooltip>
          );
        } else {
          return <Tag>{storageClass}</Tag>;
        }
      },
    },
    {
      title: 'アップロード日',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: '12%',
      render: (date: string | Date) => formatDate(date),
    },
    {
      title: '最終アクセス',
      dataIndex: 'lastAccessedAt',
      key: 'lastAccessedAt',
      width: '15%',
      render: (date: string | Date | null, record: FileInfo) => {
        if (!date) {
          return <span style={{ color: '#999' }}>未アクセス</span>;
        }

        const daysAgo = getDaysAgo(date);

        if (daysAgo > 180) {
          return (
            <Tooltip title="アーカイブ推奨">
              <Space>
                <ClockCircleOutlined style={{ color: '#faad14' }} />
                <span>{daysAgo}日前</span>
                {!record.archivedAt && (
                  <Tag color="orange" style={{ fontSize: 11 }}>
                    アーカイブ推奨
                  </Tag>
                )}
              </Space>
            </Tooltip>
          );
        }

        return <span>{daysAgo}日前</span>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: '13%',
      render: (_, record: FileInfo) => (
        <Space size="small">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            loading={downloadingFileId === record.id}
            disabled={!isAdmin}
          >
            DL
          </Button>

          {(!record.storageClass || record.storageClass === 'STANDARD') && (
            <Tooltip title="Glacier IRへアーカイブ">
              <Button
                size="small"
                icon={<SaveOutlined />}
                onClick={() => handleArchive(record)}
              />
            </Tooltip>
          )}

          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            disabled={!isAdmin}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        rowSelection={
          isAdmin
            ? {
                selectedRowKeys: selectedFiles,
                onChange: (keys) => setSelectedFiles(keys as string[]),
              }
            : undefined
        }
        columns={columns}
        dataSource={fileRows}
        loading={isLoading}
        rowKey="id"
        pagination={{
          total: data?.pagination.total,
          pageSize: data?.pagination.limit,
          current: data?.pagination.page,
          showSizeChanger: true,
          showTotal: (total) => `全${total}件`,
        }}
        size="middle"
      />

      {/* 一括操作バー */}
      {isAdmin && selectedFiles.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: '#1890ff',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
          }}
        >
          <Space size="large">
            <span style={{ color: 'white', fontWeight: 500 }}>
              選択中: {selectedFiles.length}件
            </span>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={() => message.info('一括ダウンロード機能は実装予定')}
            >
              一括DL
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
              disabled={!isAdmin}
            >
              一括削除
            </Button>
            <Button
              type="default"
              onClick={() => setSelectedFiles([])}
            >
              選択解除
            </Button>
          </Space>
        </div>
      )}
    </>
  );
}
